import { DragEvent, FC, useState } from 'react';
import { Button, Flex } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { CloudAttachment } from '@/services';
import { DragHandle, SyncingSmallTag } from '@/shared/components/drag-and-drop';
import { getDragActiveStyle } from '@/shared/components/drag-and-drop/platformStyles';
import { getHoverRevealStyle } from '@/shared/components/layouts/hoverReveal';
import CompactVirtualizedList from '@/shared/components/lists/CompactVirtualizedList';
import { MiddleDot } from '@/shared/components/typography';
import { formatDate } from '@/utilities';

import AttachmentFileAvatar from '../../../AttachmentFileAvatar';
import { AttachmentFilePresentation, formatAttachmentFileSize } from '../../useAttachmentFilePresentation';
import AttachmentItemActions from '../AttachmentItemActions';

interface AttachmentFileListItemProps {
    file: CloudAttachment;
    isDragging: boolean;
    isMoving: boolean;
    onDragEnd: () => void;
    onDragStart: (event: DragEvent<HTMLElement>, attachment: CloudAttachment) => void;
    onMoveEnabled: boolean;
    onOpen?: (attachment: CloudAttachment) => void;
    presentation?: AttachmentFilePresentation;
}

const AttachmentFileListItem: FC<AttachmentFileListItemProps> = ({
    file,
    isDragging,
    isMoving,
    onDragEnd,
    onDragStart,
    onMoveEnabled,
    onOpen,
    presentation,
}) => {
    const { token } = useAntDesignTheme();
    const [actionsOpen, setActionsOpen] = useState(false);
    const [hovered, setHovered] = useState(false);
    const itemStyle = { padding: '0.5rem 1rem' };
    const handleDragEnd = () => {
        setHovered(false);
        onDragEnd();
    };

    return (
        <CompactVirtualizedList.Item
            data-attachment-drag-preview-source={file.id}
            draggable={onMoveEnabled && !isMoving}
            hovered={hovered || isDragging}
            hoverLeading={
                onMoveEnabled ? (
                    <DragHandle
                        className='attachment-drag-handle'
                        disabled={isMoving}
                        isDragging={isDragging || isMoving}
                        label={`Move ${file.name}`}
                    />
                ) : undefined
            }
            leading={<AttachmentFileAvatar mimeType={file.mimeType} presentation={presentation} />}
            style={isDragging ? getDragActiveStyle(itemStyle, token) : itemStyle}
            onDragEnd={handleDragEnd}
            onDragStart={(event) => onDragStart(event, file)}
            onHoveredChange={setHovered}
        >
            <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                <div style={{ minWidth: 0 }}>
                    <Flex align='center' style={{ minWidth: 0 }}>
                        {onOpen ? (
                            <Button
                                type='text'
                                onClick={() => onOpen(file)}
                                style={{
                                    color: token.colorText,
                                    display: 'block',
                                    flex: '0 1 auto',
                                    height: 'auto',
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textAlign: 'left',
                                    textDecoration: 'underline',
                                    textOverflow: 'ellipsis',
                                    textUnderlineOffset: '0.125rem',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {file.name}
                            </Button>
                        ) : file.webViewLink ? (
                            <a
                                href={file.webViewLink}
                                rel='noopener noreferrer'
                                target='_blank'
                                style={{
                                    color: token.colorText,
                                    flex: '0 1 auto',
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textDecoration: 'underline',
                                    textOverflow: 'ellipsis',
                                    textUnderlineOffset: '0.125rem',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {file.name}
                            </a>
                        ) : (
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {file.name}
                            </span>
                        )}
                        {isMoving && <SyncingSmallTag />}
                    </Flex>
                </div>
                <small style={{ color: token.colorTextTertiary }}>
                    {presentation?.label ?? file.mimeType}
                    <MiddleDot horizontalMargin='0.375rem' />
                    {formatAttachmentFileSize(file.size)}
                    {file.createdTime && (
                        <>
                            <MiddleDot horizontalMargin='0.375rem' />
                            Uploaded {formatDate(new Date(file.createdTime))}
                        </>
                    )}
                </small>
            </div>
            <Flex align='center' gap='0.25rem' style={{ flex: 'none' }}>
                <span style={getHoverRevealStyle(hovered || actionsOpen || isDragging, token)}>
                    <AttachmentItemActions attachment={file} onOpen={onOpen} onOpenChange={setActionsOpen} />
                </span>
            </Flex>
        </CompactVirtualizedList.Item>
    );
};

export default AttachmentFileListItem;
